// OverlayLoader.tsx
import React, { useEffect } from "react";

interface OverlayLoaderProps {
  loading: boolean;
  message?: string;
  submessage?: string;
}

const OverlayLoader: React.FC<OverlayLoaderProps> = ({
  loading,
  message = "Loading Projects",
  submessage = "Please wait while we fetch your data...",
}) => {
  // Add keyframes to document head if they don't exist
  useEffect(() => {
    if (!document.getElementById("loader-animations")) {
      const style = document.createElement("style");
      style.id = "loader-animations";
      style.textContent = `
        @keyframes loader-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes loader-bounce {
          0%, 20%, 53%, 80%, 100% {
            animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
            transform: translate3d(0, -8px, 0);
          }
          70% {
            animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
            transform: translate3d(0, -4px, 0);
          }
          90% {
            transform: translate3d(0,-1px,0);
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!loading) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          backgroundColor: "rgb(255 255 255 / 5%)",
          borderRadius: "12px",
          padding: "32px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "20px",
          minWidth: "280px",
        }}
      >
        {/* Spinning loader */}
        <div style={{ position: "relative" }}>
          <div
            style={{
              width: "64px",
              height: "64px",
              border: "4px solid #e5e7eb",
              borderRadius: "50%",
              animation: "loader-spin 1s linear infinite",
            }}
          ></div>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "64px",
              height: "64px",
              border: "4px solid #84556b",
              borderRadius: "50%",
              borderTopColor: "transparent",
              borderRightColor: "transparent",
              animation: "loader-spin 1s linear infinite",
            }}
          ></div>
        </div>

        {/* Loading text */}
        <div style={{ textAlign: "center" }}>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
              marginBottom: "8px",
              margin: 0,
            }}
          >
            {message}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "rgb(50 54 61)",
              margin: 0,
            }}
          >
            {submessage}
          </p>
        </div>

        {/* Animated dots */}
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <div
            style={{
              width: "8px",
              height: "8px",
              backgroundColor: "#84556b",
              borderRadius: "50%",
              animation: "loader-bounce 1.4s infinite",
            }}
          ></div>
          <div
            style={{
              width: "8px",
              height: "8px",
              backgroundColor: "#84556b",
              borderRadius: "50%",
              animation: "loader-bounce 1.4s infinite",
              animationDelay: "0.2s",
            }}
          ></div>
          <div
            style={{
              width: "8px",
              height: "8px",
              backgroundColor: "#84556b",
              borderRadius: "50%",
              animation: "loader-bounce 1.4s infinite",
              animationDelay: "0.4s",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default OverlayLoader;
